import { getClassNames } from '@ab-core/functions/styles/classNameFormatter';
import useText from '@ab-core/localization/src/useText';
import type { Plugins } from '@cloudinary/html/dist';
import { AdvancedImage, lazyload } from '@cloudinary/react';
import { withPrefix } from 'gatsby';
import type { FC } from 'react';
import React, { useCallback, useState } from 'react';
import { StyledImage } from './styled';
import { useCloudinaryImage } from './utils';

enum OBJECT_FIT {
    FILL = 'fill',
    CONTAIN = 'contain',
    COVER = 'cover',
    NONE = 'none',
    SCALE_DOWN = 'scale-down'
}

export type ImageProps = React.ComponentPropsWithoutRef<'img'> & {
    objectFit?: keyof typeof OBJECT_FIT;
};

const FALLBACK_IMAGE = withPrefix('/images/products/fallbackImage.webp');

const plugins: Plugins = [lazyload({ rootMargin: '10px', threshold: 0.1 })];

export const Image: FC<ImageProps> = (props) => {
    const { src, objectFit, className, loading = 'lazy', ...rest } = props;
    const [imageError, setImageError] = useState(false);
    const removeStringsText = useText('cloudinary.removeStrings');

    const srcProp = src || FALLBACK_IMAGE;
    const objectFitProp = objectFit ? OBJECT_FIT[objectFit] : OBJECT_FIT.CONTAIN;

    const newClassName = getClassNames({
        className,
        prefix: 'image',
        modifier: { 'object-fit': objectFitProp }
    });

    const cldImg = useCloudinaryImage(srcProp, removeStringsText);
    const isCloudinaryImage = cldImg && !imageError;

    const handleImageError = useCallback(() => {
        setImageError(true);
    }, []);

    return (
        <StyledImage>
            {isCloudinaryImage && (
                <AdvancedImage
                    className={newClassName}
                    cldImg={cldImg}
                    plugins={plugins}
                    onError={handleImageError}
                    {...rest}
                />
            )}
            {!isCloudinaryImage && (
                <img
                    src={imageError ? FALLBACK_IMAGE : srcProp}
                    className={newClassName}
                    loading={loading}
                    onError={handleImageError}
                    {...rest}
                />
            )}
        </StyledImage>
    );
};
